import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Search, Bug, AlertTriangle, Trash2 } from 'lucide-react';
import './MasterAdmin.css';

const BugReportsManagement = () => {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [severityFilter, setSeverityFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchBugs();
  }, [statusFilter, severityFilter]);

  const fetchBugs = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (statusFilter !== 'All') queryParams.append('status', statusFilter);
      if (severityFilter !== 'All') queryParams.append('severity', severityFilter);
      
      const response = await api(`/master-admin/bug-reports?${queryParams.toString()}`);
      const data = await response.json();
      if (response.ok) setBugs(data);
    } catch (err) {
      console.error('Error fetching bugs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await api(`/master-admin/bug-reports/${id}/status`, {
        method: 'PUT',
        body: { status: newStatus }
      });
      if (response.ok) {
        setBugs(bugs.map(b => b._id === id ? { ...b, status: newStatus } : b));
      }
    } catch (err) {
      console.error('Error updating bug status:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bug report?')) return;
    try {
      const response = await api(`/master-admin/bug-reports/${id}`, { method: 'DELETE' });
      if (response.ok) {
        setBugs(bugs.filter(b => b._id !== id));
      }
    } catch (err) {
      console.error('Error deleting bug:', err);
    }
  };

  const filteredBugs = bugs.filter(b => 
    b.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.reporterName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.reporterEmail?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Low': return { bg: 'rgba(16,185,129,0.1)', text: '#34d399' };
      case 'Medium': return { bg: 'rgba(245,158,11,0.1)', text: '#fbbf24' };
      case 'High': return { bg: 'rgba(249,115,22,0.1)', text: '#fb923c' };
      case 'Critical': return { bg: 'rgba(239,68,68,0.1)', text: '#f87171' };
      default: return { bg: 'rgba(148,163,184,0.1)', text: '#94a3b8' };
    }
  };

  const getStatusBadge = (status) => {
    let colors = {};
    switch (status) {
      case 'Open': colors = { bg: 'rgba(239,68,68,0.2)', text: '#fca5a5' }; break;
      case 'In Review': colors = { bg: 'rgba(59,130,246,0.2)', text: '#93c5fd' }; break;
      case 'Resolved': colors = { bg: 'rgba(16,185,129,0.2)', text: '#6ee7b7' }; break;
      case 'Closed': colors = { bg: 'rgba(100,116,139,0.2)', text: '#cbd5e1' }; break;
      default: colors = { bg: 'rgba(148,163,184,0.1)', text: '#94a3b8' };
    }
    return <span className="ma-badge" style={{ background: colors.bg, color: colors.text }}>{status}</span>;
  };

  return (
    <div>
      <div className="ma-header">
        <h1>Bug Reports</h1>
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, position: 'relative', minWidth: '300px' }}>
          <Search size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--ma-text-muted)' }} />
          <input 
            type="text" 
            placeholder="Search by title or reporter..." 
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '0.75rem 1rem 0.75rem 2.5rem', borderRadius: '0.5rem', background: 'var(--ma-bg-card)', border: '1px solid var(--ma-border)', color: 'white', outline: 'none' }}
          />
        </div>
        
        <select 
          value={severityFilter} 
          onChange={e => setSeverityFilter(e.target.value)}
          style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', background: 'var(--ma-bg-card)', border: '1px solid var(--ma-border)', color: 'white', outline: 'none', cursor: 'pointer' }}
        >
          <option value="All">All Severities</option>
          <option value="Low">Low</option>
          <option value="Medium">Medium</option>
          <option value="High">High</option>
          <option value="Critical">Critical</option>
        </select>

        <select 
          value={statusFilter} 
          onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', background: 'var(--ma-bg-card)', border: '1px solid var(--ma-border)', color: 'white', outline: 'none', cursor: 'pointer' }}
        >
          <option value="All">All Statuses</option>
          <option value="Open">Open</option>
          <option value="In Review">In Review</option>
          <option value="Resolved">Resolved</option>
          <option value="Closed">Closed</option>
        </select>
      </div>

      <div className="ma-table-container">
        <table className="ma-table">
          <thead>
            <tr>
              <th>Bug Info</th>
              <th>Reporter</th>
              <th>Severity</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" style={{ textAlign: 'center' }}>Loading bug reports...</td></tr>
            ) : filteredBugs.length === 0 ? (
              <tr><td colSpan="5" style={{ textAlign: 'center' }}>No bug reports found.</td></tr>
            ) : (
              filteredBugs.map(bug => (
                <tr key={bug._id}>
                  <td style={{ maxWidth: '300px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>{bug.title}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--ma-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {bug.description}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: '600', marginBottom: '2px' }}>{bug.reporterName || 'Unknown'}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--ma-text-muted)' }}>{bug.reporterEmail}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--ma-primary)', marginTop: '2px' }}>Tenant: {bug.tenantId}</div>
                  </td>
                  <td>
                    <span style={{ 
                      padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '600',
                      background: getSeverityColor(bug.severity).bg, color: getSeverityColor(bug.severity).text 
                    }}>
                      {bug.severity}
                    </span>
                  </td>
                  <td>{getStatusBadge(bug.status)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <select 
                        value={bug.status}
                        onChange={(e) => handleStatusChange(bug._id, e.target.value)}
                        style={{ padding: '0.25rem 0.5rem', borderRadius: '0.25rem', background: 'var(--ma-bg-dark)', border: '1px solid var(--ma-border)', color: 'white', cursor: 'pointer', fontSize: '0.8rem' }}
                      >
                        <option value="Open">Open</option>
                        <option value="In Review">In Review</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Closed">Closed</option>
                      </select>
                      <button onClick={() => handleDelete(bug._id)} className="ma-btn-delete" style={{ padding: '0.25rem 0.5rem' }}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BugReportsManagement;
